/**
 * Code Module Routing
 *
 * Local routing definitions for the code module, isolated from @/state/routing.
 * Provides typesafe navigation for code-specific routes.
 */

import { createContext, createElement, type ReactNode, useContext, useMemo } from "react"
import { useNavigate as rrUseNavigate, useLocation, useParams } from "react-router"

const NEW_TASK_BACKGROUND_PATH_KEY = "newTaskBackgroundPath"
const NEW_TASK_RETURN_PATH_KEY = "newTaskReturnPath"

const CodeNavigationContext = createContext<((path: string) => void) | null>(null)

export function CodeNavigationProvider({ children, navigate }: { children: ReactNode; navigate: (path: string) => void }) {
    return createElement(CodeNavigationContext.Provider, { value: navigate }, children)
}

export interface NewTaskRouteState {
    newTaskBackgroundPath: string
    newTaskReturnPath: string
}

type RouteWithoutParams = {
    path: string
    makePath: () => string
}

type RouteWithParams<T extends Record<string, string>> = {
    path: string
    makePath: (params: T) => string
}

export const codeRoutes = {
    Code: {
        path: "/dashboard/code",
        makePath: () => "/dashboard/code",
    },
    Computers: {
        path: "/dashboard/computers",
        makePath: () => "/dashboard/computers",
    },
    CodeWorkspaceCreate: {
        path: "/dashboard/code/workspace/create",
        makePath: () => "/dashboard/code/workspace/create",
    },
    CodeWorkspace: {
        path: "/dashboard/code/workspace/:workspaceId",
        makePath: (params: { workspaceId: string }) => `/dashboard/code/workspace/${params.workspaceId}`,
    },
    CodeWorkspaceTaskCreate: {
        path: "/dashboard/code/workspace/:workspaceId/task/create",
        makePath: (params: { workspaceId: string }) => `/dashboard/code/workspace/${params.workspaceId}/task/create`,
    },
    CodeWorkspaceTaskCreating: {
        path: "/dashboard/code/workspace/:workspaceId/task/create/:creationId",
        makePath: (params: { workspaceId: string; creationId: string }) => `/dashboard/code/workspace/${params.workspaceId}/task/create/${params.creationId}`,
    },
    CodeWorkspaceTask: {
        path: "/dashboard/code/workspace/:workspaceId/task/:taskId",
        makePath: (params: { workspaceId: string; taskId: string }) => `/dashboard/code/workspace/${params.workspaceId}/task/${params.taskId}`,
    },
    CodeWorkspaceSettings: {
        path: "/dashboard/code/workspace/:workspaceId/settings",
        makePath: (params: { workspaceId: string }) => `/dashboard/code/workspace/${params.workspaceId}/settings`,
    },
    CodeWorkspaceDiffLab: {
        path: "/dashboard/code/workspace/:workspaceId/diff-lab",
        makePath: (params: { workspaceId: string }) => `/dashboard/code/workspace/${params.workspaceId}/diff-lab`,
    },
} as const

// Type utilities for extracting route categories
type CodeRoutesWithoutParams = {
    [K in keyof typeof codeRoutes]: (typeof codeRoutes)[K] extends RouteWithoutParams ? K : never
}[keyof typeof codeRoutes]

type CodeRoutesWithParams = {
    [K in keyof typeof codeRoutes]: (typeof codeRoutes)[K] extends RouteWithParams<infer _> ? K : never
}[keyof typeof codeRoutes]

type ExtractCodeRouteParams<K extends keyof typeof codeRoutes> = (typeof codeRoutes)[K] extends RouteWithParams<infer P> ? P : never

// Typesafe navigation interface
export interface CodeNavigationMethods {
    go<K extends CodeRoutesWithoutParams>(routeName: K): void
    go<K extends CodeRoutesWithParams>(routeName: K, params: ExtractCodeRouteParams<K>): void

    path<K extends CodeRoutesWithoutParams>(routeName: K): string
    path<K extends CodeRoutesWithParams>(routeName: K, params: ExtractCodeRouteParams<K>): string
}

export type CodeNavigator = CodeNavigationMethods & {
    goPath: (path: string) => void
    openTaskCreate: (workspaceId: string) => void
    switchTaskCreateWorkspace: (workspaceId: string) => void
    dismissTaskCreate: () => void
    params: ReturnType<typeof useParams>
    location: ReturnType<typeof useLocation>
}

export function getNewTaskRouteState(location: Pick<ReturnType<typeof useLocation>, "state">): NewTaskRouteState | null {
    const state: unknown = location.state
    if (typeof state !== "object" || state === null || Array.isArray(state)) return null

    const backgroundPath = Reflect.get(state, NEW_TASK_BACKGROUND_PATH_KEY)
    if (typeof backgroundPath !== "string" || !backgroundPath.startsWith("/")) return null
    return {
        newTaskBackgroundPath: backgroundPath,
        newTaskReturnPath: getNewTaskReturnPath(location) ?? backgroundPath,
    }
}

export function getNewTaskReturnPath(location: Pick<ReturnType<typeof useLocation>, "state">): string | null {
    const state: unknown = location.state
    if (typeof state !== "object" || state === null || Array.isArray(state)) return null
    const returnPath = Reflect.get(state, NEW_TASK_RETURN_PATH_KEY)
    return typeof returnPath === "string" && returnPath.startsWith("/") ? returnPath : null
}

export function currentLocationPath(location: ReturnType<typeof useLocation>): string {
    return `${location.pathname}${location.search}${location.hash}`
}

export const useCodeNavigate = (): CodeNavigator => {
    const navigate = rrUseNavigate()
    const location = useLocation()
    const params = useParams()
    const scopedNavigate = useContext(CodeNavigationContext)

    return useMemo(() => {
        const existingNewTaskState = getNewTaskRouteState(location)
        const goPath = scopedNavigate ?? ((path: string) => navigate(path))
        const taskCreatePath = (workspaceId: string) => codeRoutes.CodeWorkspaceTaskCreate.makePath({ workspaceId })
        const navigationMethods: CodeNavigationMethods = {
            go: (routeName: keyof typeof codeRoutes, params?: Record<string, string>) => {
                const route = codeRoutes[routeName]
                if (params) {
                    goPath((route as RouteWithParams<Record<string, string>>).makePath(params))
                } else {
                    goPath((route as RouteWithoutParams).makePath())
                }
            },

            path: (routeName: keyof typeof codeRoutes, params?: Record<string, string>) => {
                const route = codeRoutes[routeName]
                if (params) {
                    return (route as RouteWithParams<Record<string, string>>).makePath(params)
                } else {
                    return (route as RouteWithoutParams).makePath()
                }
            },
        }

        return {
            ...navigationMethods,
            goPath,
            openTaskCreate: (workspaceId: string) => {
                const path = taskCreatePath(workspaceId)
                if (location.pathname === path) return

                navigate(path, {
                    state: {
                        [NEW_TASK_BACKGROUND_PATH_KEY]: existingNewTaskState?.newTaskBackgroundPath ?? currentLocationPath(location),
                        [NEW_TASK_RETURN_PATH_KEY]: existingNewTaskState?.newTaskReturnPath ?? currentLocationPath(location),
                    },
                })
            },
            switchTaskCreateWorkspace: (workspaceId: string) => {
                const path = taskCreatePath(workspaceId)
                if (location.pathname === path) return

                navigate(path, {
                    replace: true,
                    state: existingNewTaskState ?? undefined,
                })
            },
            dismissTaskCreate: () => {
                if (existingNewTaskState) {
                    navigate(-1)
                    return
                }
                navigate(codeRoutes.Code.makePath())
            },
            params,
            location,
        }
    }, [navigate, params, location, scopedNavigate])
}
